import React from 'react';
import { Link } from 'react-router-dom';
import { Users, ArrowLeft, Zap, Target, Brain, Award, Shield, BookOpen } from 'lucide-react';
import { GridBackground } from '../components/TechUI';
import RastorLogo from '../components/RastorLogo';
import SEO from '../components/SEO';

const AboutUsPage = () => {
    return (
        <GridBackground className="min-h-screen">
            <SEO
                title="About Us | Rastor AI"
                description="About Rastor AI — AI-powered career coaching platform helping professionals, students, and career changers navigate their career journey with personalized roadmaps."
            />
            <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                <Link
                    to="/"
                    className="inline-flex items-center text-slate-400 hover:text-emerald-400 transition-colors font-mono text-xs uppercase tracking-wider mb-8"
                >
                    <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
                </Link>

                <div className="flex items-center space-x-4 mb-8">
                    <div className="w-12 h-12 border border-emerald-500/50 bg-emerald-500/10 rounded-xl flex items-center justify-center">
                        <Users className="w-6 h-6 text-emerald-400" />
                    </div>
                    <div>
                        <h1 className="text-3xl font-bold text-white font-mono">ABOUT US</h1>
                        <p className="text-slate-500 text-xs font-mono uppercase mt-1">Rastor AI — Your AI Career Co-Pilot</p>
                    </div>
                </div>

                <div className="bg-[#0F131F]/60 border border-white/5 rounded-2xl p-8 md:p-10 space-y-10 backdrop-blur-sm">
                    {/* Hero Section */}
                    <section className="text-center pb-8 border-b border-white/5">
                        <div className="w-20 h-20 border-2 border-emerald-500/50 bg-emerald-500/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                            <RastorLogo size={40} />
                        </div>
                        <h2 className="text-2xl font-bold text-white font-mono mb-4">RASTOR AI</h2>
                        <p className="text-slate-300 text-sm leading-relaxed font-mono max-w-2xl mx-auto">
                            Rastor AI is an AI-powered career coaching platform designed to help professionals, students, and career changers navigate their career journey with precision. We combine cutting-edge artificial intelligence with deep career domain expertise to deliver hyper-personalized roadmaps, actionable insights, and real-time guidance.
                        </p>
                    </section>

                    {/* Company Info */}
                    <section>
                        <h2 className="text-lg font-bold text-emerald-400 font-mono uppercase mb-3">Registered Business</h2>
                        <div className="bg-white/5 border border-white/10 rounded-xl p-5 font-mono text-sm space-y-2">
                            <p className="text-slate-300"><span className="text-slate-500">Registered Name:</span> <strong className="text-white">Rastor AI</strong></p>
                            <p className="text-slate-300"><span className="text-slate-500">Website:</span> <Link to="/" className="text-emerald-400 hover:underline">www.rastorai.com</Link></p>
                            <p className="text-slate-300"><span className="text-slate-500">Email:</span> <a href="mailto:support@rastorai.com" className="text-emerald-400 hover:underline">support@rastorai.com</a></p>
                            <p className="text-slate-300"><span className="text-slate-500">Phone:</span> <a href="tel:+918130829610" className="text-emerald-400 hover:underline">+91 8130829610</a></p>
                            <p className="text-slate-300"><span className="text-slate-500">Location:</span> India</p>
                        </div>
                    </section>

                    {/* Mission */}
                    <section>
                        <h2 className="text-lg font-bold text-emerald-400 font-mono uppercase mb-3">Our Mission</h2>
                        <p className="text-slate-300 text-sm leading-relaxed font-mono">
                            We believe that everyone deserves access to world-class career guidance — not just those who can afford expensive coaching or are lucky enough to have the right connections. Our mission is to democratize career coaching using AI, making expert-level career intelligence accessible to anyone, anywhere, at any stage of their career.
                        </p>
                    </section>

                    {/* What We Offer */}
                    <section>
                        <h2 className="text-lg font-bold text-emerald-400 font-mono uppercase mb-4">What We Offer</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {[
                                { icon: Target, title: 'AI Career Roadmaps', desc: 'Personalized week-by-week career transition and growth plans generated by AI based on your unique profile.' },
                                { icon: Brain, title: 'AI Career Coach', desc: 'Real-time conversational AI coach that answers your career questions with personalized, context-aware advice.' },
                                { icon: BookOpen, title: 'Resume Builder & Analysis', desc: 'AI-powered resume analysis, ATS optimization, and professional resume generation tools.' },
                                { icon: Award, title: 'Skill Assessments & Certifications', desc: 'Validate your skills with AI-generated assessments and earn verifiable micro-certifications.' },
                                { icon: Zap, title: 'Career Explorer', desc: 'Explore trending careers, salary trajectories, and skill demand across industries with real-time data.' },
                                { icon: Shield, title: 'Enterprise & Education', desc: 'B2B solutions for universities and companies to track cohort progress and provide structured career guidance.' },
                            ].map((item, i) => (
                                <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-5 hover:border-emerald-500/30 transition-colors">
                                    <item.icon className="w-8 h-8 text-emerald-400 mb-3" />
                                    <h3 className="text-white font-bold font-mono text-sm mb-2">{item.title}</h3>
                                    <p className="text-slate-400 text-xs font-mono leading-relaxed">{item.desc}</p>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* Why Choose Us */}
                    <section>
                        <h2 className="text-lg font-bold text-emerald-400 font-mono uppercase mb-3">Why Choose Rastor AI?</h2>
                        <ul className="space-y-3 text-slate-300 text-sm font-mono">
                            <li className="flex items-start gap-3">
                                <span className="text-emerald-400 mt-0.5">▹</span>
                                <span><strong className="text-white">Hyper-Personalized:</strong> Every recommendation is tailored to your unique skills, experience, goals, and constraints — not generic advice.</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="text-emerald-400 mt-0.5">▹</span>
                                <span><strong className="text-white">AI-First:</strong> Powered by advanced language models that understand career trajectories, industry trends, and skill adjacencies.</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="text-emerald-400 mt-0.5">▹</span>
                                <span><strong className="text-white">Actionable:</strong> We don't just tell you where to go — we give you a concrete, week-by-week plan to get there with resources and milestones.</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="text-emerald-400 mt-0.5">▹</span>
                                <span><strong className="text-white">Affordable:</strong> Enterprise-grade career intelligence at a fraction of the cost of human career coaches.</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="text-emerald-400 mt-0.5">▹</span>
                                <span><strong className="text-white">Secure:</strong> 256-bit encryption, Firebase security, PCI-DSS compliant payments. Your data is yours.</span>
                            </li>
                        </ul>
                    </section>

                    {/* Contact CTA */}
                    <section className="text-center pt-6 border-t border-white/5">
                        <p className="text-slate-400 text-sm font-mono mb-4">Have questions or want to learn more?</p>
                        <Link
                            to="/contact"
                            className="inline-flex items-center px-8 py-3 bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 rounded-xl font-mono font-bold hover:bg-emerald-500/30 transition-all"
                        >
                            Contact Us →
                        </Link>
                    </section>
                </div>
            </div>
        </GridBackground>
    );
};

export default AboutUsPage;
